import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vansAPI, itemsAPI, adjustmentsAPI, usersAPI, vehicleCommentsAPI, vehicleToVehicleAPI, inventoryTemplatesAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { TruncatedTextWithTooltip } from '../components/TruncatedTextWithTooltip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { useInventoryView } from '../hooks/useInventoryView';
import { useLiveRefresh } from '../hooks/useLiveRefresh';
import { VehicleInventoryToolbar, useVehicleInventoryFilter } from '../components/VehicleInventoryToolbar';
import { AsyncSearchableSelect } from '../components/ui/async-searchable-select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Package, Minus, Plus, Loader2, Edit2, X, AlertTriangle, ArrowRightLeft, Download, Layers, Wrench, Users, UserPlus, MessageSquare, Send, User, Clock, ScanLine, Undo2, Settings2, Truck, MinusCircle, Home, Gauge, ClipboardCheck, History, ChevronDown, ClipboardList, Info, Save, FileDown, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { AvailabilityBreakdownModal, useAvailabilityBreakdown } from '../components/AvailabilityBreakdownModal';
import { format } from 'date-fns';
import { ScanManager } from '../components/ScanManager';
import { ReturnToWarehouseModal } from '../components/modals/ReturnToWarehouseModal';
import { ManualAdjustmentModal } from '../components/modals/ManualAdjustmentModal';
import { VehicleToVehicleTransferModal } from '../components/modals/VehicleToVehicleTransferModal';
import { StartCycleCountModal } from '../components/modals/StartCycleCountModal';
import { CycleCountHistory } from '../components/cycle-count/CycleCountHistory';
import { LastCycleCountIndicator } from '../components/cycle-count/LastCycleCountIndicator';
import { LowStockAlerts } from '../components/LowStockAlerts';
import { VanRestockReviewDrawer } from '../components/VanRestockReviewDrawer';
import { SaveTemplateModal } from '../components/SaveTemplateModal';
import { ApplyTemplateModal } from '../components/ApplyTemplateModal';
import { CloneInventoryModal } from '../components/CloneInventoryModal';
import { lowStockAPI } from '../lib/api';

/** Slim restock suggestions banner for inside the Inventory card */
function RestockSuggestionsBanner({ sourceType, sourceId }) {
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await lowStockAPI.restockSuggestions(sourceType ? { source_type: sourceType } : {});
        setSuggestions((res.data || []).filter(i => !sourceId || i.source_id === sourceId));
      } catch { /* non-critical */ }
    })();
  }, [sourceType, sourceId]);
  if (suggestions.length === 0) return null;
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-blue-50/60 dark:bg-blue-950/20 border-b border-blue-200/50 dark:border-blue-800/30 text-sm" data-testid="restock-banner">
      <Package className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" />
      <span className="text-blue-700 dark:text-blue-400 font-semibold shrink-0">Restock ({suggestions.length})</span>
      <TruncatedTextWithTooltip
        text={
          (suggestions.slice(0, 2).map(s => s.item_name).join(', ')) +
          (suggestions.length > 2 ? ` +${suggestions.length - 2} more` : '')
        }
        className="text-muted-foreground"
      />
      <span className="ml-auto text-xs text-muted-foreground shrink-0">Based on usage velocity</span>
    </div>
  );
}

export const VanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isTech, permissions } = useAuth();
  const vView = useInventoryView('vehicle');
  const vCol = vView.isVisible;
  
  // Check if user can manage cycle counts
  const canManageCycleCounts = isOwner || permissions?.['audit.manage'] === true;
  
  const [van, setVan] = useState(null);
  const [inventory, setInventory] = useState([]);
  const vFilter = useVehicleInventoryFilter(inventory, vView);
  const [allVans, setAllVans] = useState([]);
  const [allVansRaw, setAllVansRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [breakdownState, openBreakdown, setBreakdownOpen] = useAvailabilityBreakdown();
  
  // Technicians state
  const [assignedTechs, setAssignedTechs] = useState([]);
  const [allTechs, setAllTechs] = useState([]);
  const [techModalOpen, setTechModalOpen] = useState(false);
  const [selectedTechIds, setSelectedTechIds] = useState([]);
  const [savingTechs, setSavingTechs] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  // Modal state
  const [actionModal, setActionModal] = useState({ open: false, type: null });
  const [selectedItemId, setSelectedItemId] = useState('');
  const [createPartName, setCreatePartName] = useState('');
  const [selectedToVanId, setSelectedToVanId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Bulk restock state
  const [bulkItems, setBulkItems] = useState([{ item_id: '', name: '', quantity: '', note: '' }]);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  
  // Return to Warehouse modal state
  const [returnModal, setReturnModal] = useState({ open: false, preSelectedItemId: null });
  
  // Manual Adjustment modal state
  const [adjustmentModal, setAdjustmentModal] = useState({ 
    open: false, 
    itemId: null, 
    itemName: '', 
    currentQty: 0 
  });

  // Threshold Override modal state (owner-only)
  const [thresholdModal, setThresholdModal] = useState({
    open: false,
    itemId: null,
    itemName: '',
    currentOverride: null,
    itemDefault: null
  });
  const [thresholdValue, setThresholdValue] = useState('');
  const [savingThreshold, setSavingThreshold] = useState(false);

  // Vehicle-to-Vehicle Transfer Request modal state (for techs)
  const [v2vTransferModal, setV2vTransferModal] = useState({ open: false, preSelectedItemId: null });
  const [otherVans, setOtherVans] = useState([]);

  // Cycle count modal state
  const [cycleCountModalOpen, setCycleCountModalOpen] = useState(false);

  // Auto Restock drawer state (Stage 11)
  const [restockDrawerOpen, setRestockDrawerOpen] = useState(false);

  // Template modal state
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [applyTemplateOpen, setApplyTemplateOpen] = useState(false);
  const [cloneInventoryOpen, setCloneInventoryOpen] = useState(false);

  // FAB menu state (mobile only)
  const [fabMenuOpen, setFabMenuOpen] = useState(false);

  // Long-press tooltip state (mobile only)
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });
  const longPressTimer = useRef(null);
  const tooltipDismissTimer = useRef(null);

  // Long-press handlers for mobile icon buttons
  const handleTouchStart = (e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    longPressTimer.current = setTimeout(() => {
      setTooltip({
        visible: true,
        text: label,
        x: rect.left + rect.width / 2,
        y: rect.top - 8
      });
      // Auto-dismiss after 1.5s
      tooltipDismissTimer.current = setTimeout(() => {
        setTooltip(prev => ({ ...prev, visible: false }));
      }, 1500);
    }, 450);
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const dismissTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
    if (tooltipDismissTimer.current) {
      clearTimeout(tooltipDismissTimer.current);
    }
  };

  // Dismiss tooltip on scroll
  useEffect(() => {
    const handleScroll = () => dismissTooltip();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const requests = [
        vansAPI.get(id),
        vansAPI.getInventory(id),
        vehicleCommentsAPI.list(id),
      ];
      
      if (isOwner) {
        // Items are now loaded via async search, not preloaded (supports 18k+ items)
        requests.push(vansAPI.list());
        requests.push(vansAPI.getTechnicians(id));
        requests.push(usersAPI.list());
      } else if (isTech) {
        // Techs need van list for vehicle-to-vehicle transfers
        requests.push(vansAPI.list());
      }
      
      const results = await Promise.all(requests);
      setVan(results[0].data);
      setInventory(results[1].data);
      setComments(results[2].data);
      
      if (isOwner && results[3]) {
        setAllVansRaw(results[3].data || []);
        setAllVans(results[3].data.filter(v => v.id !== id && v.is_active));
      }
      if (isOwner && results[4]) {
        setAssignedTechs(results[4].data);
      }
      if (isOwner && results[5]) {
        // Filter to only techs
        setAllTechs(results[5].data.filter(u => u.role === 'tech'));
      }
      // For techs: get other vans for transfer requests
      if (isTech && results[3]) {
        setOtherVans(results[3].data.filter(v => v.id !== id && v.is_active));
      }
      
      setEditData({
        name: results[0].data.name,
        van_number: results[0].data.van_number || '',
        license_plate: results[0].data.license_plate || '',
        is_active: results[0].data.is_active,
      });
    } catch (error) {
      toast.error('Failed to load vehicle details');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isOwner, isTech]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Phase 6: background refresh updates the stock list only (never the edit form, never navigates away)
  useLiveRefresh(async () => {
    try {
      const [vanRes, invRes] = await Promise.all([vansAPI.get(id), vansAPI.getInventory(id)]);
      setVan(vanRes.data);
      setInventory(invRes.data);
    } catch (e) {
      console.warn('[VAN_REFRESH] background refresh failed', e?.message);
    }
  });
  
  // Async item search handler for Add Stock modal (supports 18k+ items)
  const handleItemSearch = useCallback(async (query, signal) => {
    try {
      const response = await itemsAPI.search(query, 25, signal);
      return (response.data || []).map(item => ({
        value: item.id,
        label: `${item.name}${item.sku ? ` (${item.sku})` : ''}`,
        sublabel: item.category || undefined
      }));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Item search error:', error);
      }
      return [];
    }
  }, []);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const response = await vansAPI.update(id, editData);
      setVan(response.data);
      setEditDrawerOpen(false);
      toast.success('Vehicle updated successfully');
    } catch (error) {
      toast.error('Failed to update vehicle');
    } finally {
      setSaving(false);
    }
  };

  const openTechModal = () => {
    setSelectedTechIds(assignedTechs.map(t => t.id));
    setTechModalOpen(true);
  };

  const toggleTechSelection = (techId) => {
    setSelectedTechIds(prev => 
      prev.includes(techId) 
        ? prev.filter(id => id !== techId)
        : [...prev, techId]
    );
  };

  const handleSaveTechAssignments = async () => {
    setSavingTechs(true);
    try {
      const response = await vansAPI.assignTechnicians(id, selectedTechIds);
      setAssignedTechs(response.data);
      setTechModalOpen(false);
      toast.success('Technician assignments updated');
    } catch (error) {
      toast.error('Failed to update assignments');
    } finally {
      setSavingTechs(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setSubmittingComment(true);
    try {
      const res = await vehicleCommentsAPI.add(id, newComment.trim());
      setComments([...comments, res.data]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle opening threshold edit modal
  const openThresholdModal = (inv) => {
    setThresholdModal({
      open: true,
      itemId: inv.item_id,
      itemName: inv.item_name,
      currentOverride: inv.reorder_threshold_override,
      itemDefault: inv.item_reorder_threshold
    });
    setThresholdValue(inv.reorder_threshold_override?.toString() || '');
  };

  // Handle saving threshold override
  const handleSaveThreshold = async () => {
    setSavingThreshold(true);
    try {
      const override = thresholdValue === '' ? null : parseInt(thresholdValue);
      await vansAPI.updateItemThreshold(id, thresholdModal.itemId, override);
      
      // Refresh inventory to get updated thresholds
      const invResponse = await vansAPI.getInventory(id);
      setInventory(invResponse.data);
      
      setThresholdModal({ open: false, itemId: null, itemName: '', currentOverride: null, itemDefault: null });
      toast.success(override !== null 
        ? `Threshold set to ${override} for this vehicle` 
        : 'Threshold reset to item default'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update threshold');
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleAction = async () => {
    if (actionModal.type === 'bulk-restock') {
      await handleBulkRestock();
      return;
    }
    
    if (actionModal.type === 'adjust') {
      await handleAdjustRequest();
      return;
    }

    // For restock: either selectedItemId or createPartName must be set
    const hasItem = selectedItemId || createPartName;
    if (!hasItem || !quantity || parseInt(quantity) < 1) {
      toast.error('Please select an item and enter a valid quantity');
      return;
    }

    if (actionModal.type === 'transfer' && !selectedToVanId) {
      toast.error('Please select a destination vehicle');
      return;
    }

    // Check if use will go negative and require note
    if (actionModal.type === 'use') {
      const invItem = inventory.find(i => i.item_id === selectedItemId);
      const willGoNegative = invItem && (invItem.qty_on_hand - parseInt(quantity)) < 0;
      if (willGoNegative && !note) {
        toast.error('Note required when using more than available quantity');
        return;
      }
    }

    setActionLoading(true);
    try {
      if (actionModal.type === 'transfer') {
        await vansAPI.transfer({
          from_van_id: id,
          to_van_id: selectedToVanId,
          item_id: selectedItemId,
          quantity: parseInt(quantity),
          note: note || null,
        });
        toast.success('Transfer successful');
      } else {
        const data = createPartName
          ? { name: createPartName, quantity: parseInt(quantity), note: note || null }
          : { item_id: selectedItemId, quantity: parseInt(quantity), note: note || null };

        if (actionModal.type === 'use') {
          await vansAPI.useItem(id, data);
          toast.success('Item used successfully');
        } else {
          const resp = await vansAPI.restockItem(id, data);
          toast.success(resp.data?.created
            ? 'Part created and added successfully'
            : 'Stock updated successfully');
        }
      }

      closeModal();
      fetchData();
    } catch (error) {
      // Check if action was queued for offline sync
      if (error.isQueued) {
        toast.info('You\'re offline. Action queued for sync when back online.');
        closeModal();
      } else {
        toast.error(error.response?.data?.detail || 'Action failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustRequest = async () => {
    if (!selectedItemId || !quantity || parseInt(quantity) === 0 || !note) {
      toast.error('Please fill in all fields. Note is required.');
      return;
    }

    setActionLoading(true);
    try {
      await adjustmentsAPI.create({
        van_id: id,
        item_id: selectedItemId,
        qty_delta: parseInt(quantity),
        note: note,
      });
      toast.success('Adjustment request submitted for approval');
      closeModal();
    } catch (error) {
      if (error.isQueued) {
        toast.info('You\'re offline. Action queued for sync when back online.');
        closeModal();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit adjustment');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkRestock = async () => {
    const validItems = bulkItems.filter(item => (item.item_id || item.name) && parseInt(item.quantity) > 0);
    if (validItems.length === 0) {
      toast.error('Add at least one item with quantity');
      return;
    }

    setActionLoading(true);
    try {
      await vansAPI.bulkRestock(id, {
        items: validItems.map(item => ({
          ...(item.item_id ? { item_id: item.item_id } : { name: item.name }),
          quantity: parseInt(item.quantity),
          note: item.note || null,
        }))
      });
      toast.success(`${validItems.length} items restocked`);
      closeModal();
      fetchData();
    } catch (error) {
      if (error.isQueued) {
        toast.info('You\'re offline. Action queued for sync when back online.');
        closeModal();
      } else {
        toast.error(error.response?.data?.detail || 'Bulk restock failed');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const openActionModal = (type) => {
    setActionModal({ open: true, type });
    setSelectedItemId('');
    setCreatePartName('');
    setSelectedToVanId('');
    setQuantity(type === 'adjust' ? '0' : '1');
    setNote('');
    setBulkItems([{ item_id: '', name: '', quantity: '', note: '' }]);
  };

  const closeModal = () => {
    setActionModal({ open: false, type: null });
    setSelectedItemId('');
    setCreatePartName('');
    setSelectedToVanId('');
    setQuantity('1');
    setNote('');
    setBulkItems([{ item_id: '', name: '', quantity: '', note: '' }]);
  };

  const addBulkItem = () => {
    setBulkItems([...bulkItems, { item_id: '', name: '', quantity: '', note: '' }]);
  };

  const updateBulkItem = (index, field, value) => {
    const updated = [...bulkItems];
    updated[index][field] = value;
    setBulkItems(updated);
  };

  const removeBulkItem = (index) => {
    if (bulkItems.length > 1) {
      setBulkItems(bulkItems.filter((_, i) => i !== index));
    }
  };

  const handleExport = async () => {
    try {
      const response = await vansAPI.exportInventory(id);
      
      // Create blob download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${van?.name || 'van'}_inventory_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Inventory exported successfully');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only owners can export inventory');
      } else {
        toast.error(error.response?.data?.detail || 'Export failed');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!van) return null;

  return (
    <Layout>
      <div className="space-y-4 pb-24 md:pb-0 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          {/* Top row: back button + title + mobile kebab */}
          <div className="flex items-start gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0 h-9 w-9 md:h-10 md:w-10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl md:text-3xl font-bold font-heading text-foreground truncate">{van.name}</h1>
                {isOwner && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-7 w-7 md:h-8 md:w-8 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setEditData({
                        name: van.name,
                        van_number: van.van_number || '',
                        license_plate: van.license_plate || '',
                        is_active: van.is_active,
                      });
                      setEditDrawerOpen(true);
                    }}
                    data-testid="edit-van-btn"
                    title="Edit Vehicle"
                  >
                    <Edit2 className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  </Button>
                )}
              </div>
              {/* Vehicle details + status badge */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
                {van.van_number && <span>#{van.van_number}</span>}
                {van.van_number && van.license_plate && <span className="hidden md:inline">·</span>}
                {van.license_plate && <span>{van.license_plate}</span>}
                {/* Cycle Count status badge */}
                {canManageCycleCounts && (
                  <LastCycleCountIndicator 
                    targetType="vehicle" 
                    targetId={id}
                    className="ml-0.5"
                    badge
                  />
                )}
              </div>
              {/* Technician avatars row */}
              {isOwner && (
                <div className="flex items-center gap-1.5 mt-1" data-testid="tech-avatar-row">
                  {assignedTechs.slice(0, 5).map((tech, idx) => {
                    const colors = [
                      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
                      'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
                      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
                      'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
                      'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
                    ];
                    const initials = tech.name ? (tech.name.trim().split(/\s+/).length >= 2
                      ? (tech.name.trim().split(/\s+/)[0][0] + tech.name.trim().split(/\s+/).slice(-1)[0][0]).toUpperCase()
                      : tech.name.trim()[0].toUpperCase()) : '?';
                    return (
                      <div
                        key={tech.id}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${colors[idx % colors.length]}`}
                        title={tech.name}
                        data-testid={`tech-avatar-${tech.id}`}
                      >
                        {initials}
                      </div>
                    );
                  })}
                  {assignedTechs.length > 5 && (
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-muted text-muted-foreground"
                      title={`${assignedTechs.length - 5} more`}
                      data-testid="tech-avatar-overflow"
                    >
                      +{assignedTechs.length - 5}
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground border-dashed"
                    onClick={openTechModal}
                    data-testid="assign-techs-btn"
                  >
                    <UserPlus className="w-3 h-3 mr-1" />
                    Assign
                  </Button>
                </div>
              )}
              {/* Compact stats row — low stock / negative indicators only */}
              {(() => {
                const lowCount = inventory.filter(inv => inv.reorder_threshold !== null && inv.qty_on_hand <= inv.reorder_threshold && inv.qty_on_hand >= 0).length;
                const negCount = inventory.filter(inv => inv.is_negative || inv.qty_on_hand < 0).length;
                if (lowCount === 0 && negCount === 0) return null;
                return (
                  <div className="flex items-center gap-x-3 mt-1 text-xs text-muted-foreground" data-testid="header-stats-row">
                    {lowCount > 0 && (
                      <span className="font-medium tabular-nums text-amber-600 dark:text-amber-400" data-testid="stat-low-stock">{lowCount} Low Stock</span>
                    )}
                    {lowCount > 0 && negCount > 0 && <span className="text-border">·</span>}
                    {negCount > 0 && (
                      <span className="font-medium tabular-nums text-destructive" data-testid="stat-negative">{negCount} Negative</span>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          
          {/* Actions row */}
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setScannerOpen(true)} title="Scan barcode">
              <ScanLine className="w-4 h-4 md:w-5 md:h-5" />
            </Button>
            {isOwner && (
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleExport} title="Export CSV" data-testid="export-btn">
                <Download className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
            )}
            {/* Cycle Count button - visible on all sizes */}
            {canManageCycleCounts && (
              <Button 
                variant="default" 
                onClick={() => setCycleCountModalOpen(true)} 
                className="gap-2 h-9"
                data-testid="start-cycle-count-btn"
              >
                <ClipboardCheck className="w-4 h-4" />
                <span className="hidden sm:inline">Start</span> Cycle Count
              </Button>
            )}
          </div>
        </div>

        {/* Desktop Actions — single row */}
        <div className="hidden md:flex flex-wrap items-center gap-2">
          <Button
            className="h-10 px-5 font-bold btn-press"
            onClick={() => openActionModal('restock')}
            data-testid="add-stock-btn"
          >
            <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
            Add Stock
          </Button>
          <Button
            variant="outline"
            className="h-10 px-5 font-bold hover:bg-destructive/10 hover:border-destructive hover:text-destructive"
            onClick={() => openActionModal('use')}
            data-testid="use-item-btn"
          >
            <Minus className="w-4 h-4 mr-2 flex-shrink-0" />
            Use Item
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10 px-4 font-semibold"
                data-testid="inventory-actions-dropdown"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2 flex-shrink-0" />
                Inventory Actions
                <ChevronDown className="w-3.5 h-3.5 ml-1.5 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              {/* Group 1 — Inventory Movement */}
              {isTech ? (
                <DropdownMenuItem 
                  onClick={() => setV2vTransferModal({ open: true, preSelectedItemId: null })}
                  className="cursor-pointer"
                  data-testid="dropdown-transfer-to-vehicle"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Transfer to Vehicle
                </DropdownMenuItem>
              ) : isOwner && (
                <DropdownMenuItem 
                  onClick={() => openActionModal('transfer')}
                  className="cursor-pointer"
                  data-testid="dropdown-transfer"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer to Vehicle
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={() => setReturnModal({ open: true, preSelectedItemId: null })}
                className="cursor-pointer"
                data-testid="dropdown-return-to-wh"
              >
                <Undo2 className="w-4 h-4 mr-2" />
                Return to WH
              </DropdownMenuItem>

              {/* Group 2 — Bulk Actions */}
              {isOwner && <DropdownMenuSeparator />}
              {isOwner && (
                <DropdownMenuItem 
                  onClick={() => openActionModal('bulk-restock')}
                  className="cursor-pointer"
                  data-testid="dropdown-bulk-restock"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Bulk Restock
                </DropdownMenuItem>
              )}

              {/* Group 3 — Templates & System */}
              {isOwner && <DropdownMenuSeparator />}
              {isOwner && allVans.length > 0 && (
                <DropdownMenuItem 
                  onClick={() => setCloneInventoryOpen(true)}
                  className="cursor-pointer"
                  data-testid="dropdown-clone-inventory"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Clone from Vehicle
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem 
                  onClick={() => setApplyTemplateOpen(true)}
                  className="cursor-pointer"
                  data-testid="dropdown-apply-template"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Apply Template
                </DropdownMenuItem>
              )}
              {isOwner && inventory.length > 0 && (
                <DropdownMenuItem 
                  onClick={() => setSaveTemplateOpen(true)}
                  className="cursor-pointer"
                  data-testid="dropdown-save-template"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save as Template
                </DropdownMenuItem>
              )}
              {isOwner && (
                <DropdownMenuItem 
                  onClick={() => setRestockDrawerOpen(true)}
                  className="cursor-pointer"
                  data-testid="dropdown-auto-restock"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  Auto Restock
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Request Adjust - Tech only */}
          {isTech && (
            <Button
              variant="outline"
              className="h-10 px-4 font-semibold"
              onClick={() => openActionModal('adjust')}
              data-testid="adjust-btn"
            >
              <Wrench className="w-4 h-4 mr-2 flex-shrink-0" />
              Request Adjust
            </Button>
          )}
        </div>

        {/* Action Buttons - Mobile (Icon-only, max 4) */}
        <div className="md:hidden px-2">
          {/* Long-press Tooltip */}
          {tooltip.visible && (
            <div 
              className="fixed z-[100] px-3 py-1.5 bg-foreground text-background text-xs font-medium rounded-md shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
              style={{ left: tooltip.x, top: tooltip.y }}
            >
              {tooltip.text}
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground" />
            </div>
          )}

          <div className="flex justify-around">
            {/* 1. Use Item - Destructive styling */}
            <Button
              variant="outline"
              size="icon"
              className="h-14 w-14 border-2 bg-destructive/10 border-destructive/30 text-destructive hover:bg-destructive/20 hover:border-destructive"
              onClick={() => openActionModal('use')}
              onTouchStart={(e) => handleTouchStart(e, 'Use Item')}
              onTouchEnd={handleTouchEnd}
              aria-label="Use Item"
            >
              <MinusCircle className="w-6 h-6" />
            </Button>
            
            {/* 2. Transfer (Owner) or Transfer to Vehicle (Tech) */}
            {isOwner && (
              <Button
                variant="outline"
                size="icon"
                className="h-14 w-14 border-2"
                onClick={() => openActionModal('transfer')}
                onTouchStart={(e) => handleTouchStart(e, 'Transfer')}
                onTouchEnd={handleTouchEnd}
                aria-label="Transfer"
              >
                <ArrowRightLeft className="w-6 h-6" />
              </Button>
            )}
            {isTech && (
              <Button
                variant="outline"
                size="icon"
                className="h-14 w-14 border-2 text-primary border-primary/50 hover:bg-primary/10 hover:border-primary"
                onClick={() => setV2vTransferModal({ open: true, preSelectedItemId: null })}
                onTouchStart={(e) => handleTouchStart(e, 'Transfer to Vehicle')}
                onTouchEnd={handleTouchEnd}
                aria-label="Transfer to Vehicle"
              >
                <Truck className="w-6 h-6" />
              </Button>
            )}
            
            {/* 3. Return to Warehouse */}
            <Button
              variant="outline"
              size="icon"
              className="h-14 w-14 border-2"
              onClick={() => setReturnModal({ open: true, preSelectedItemId: null })}
              onTouchStart={(e) => handleTouchStart(e, 'Return to Warehouse')}
              onTouchEnd={handleTouchEnd}
              aria-label="Return to Warehouse"
            >
              <Home className="w-6 h-6" />
            </Button>

            {/* 4. Request Adjust (Tech only - 4th slot) */}
            {isTech && (
              <Button
                variant="outline"
                size="icon"
                className="h-14 w-14 border-2"
                onClick={() => openActionModal('adjust')}
                onTouchStart={(e) => handleTouchStart(e, 'Request Adjustment')}
                onTouchEnd={handleTouchEnd}
                aria-label="Request Adjustment"
              >
                <Wrench className="w-6 h-6" />
              </Button>
            )}
          </div>
        </div>

        {/* Mobile FAB + Menu */}
        <div className="md:hidden">
          {/* FAB Menu Overlay */}
          {fabMenuOpen && (
            <div 
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setFabMenuOpen(false)}
            />
          )}
          
          {/* FAB Menu Items */}
          {fabMenuOpen && (
            <div className="fixed bottom-[calc(7.5rem+env(safe-area-inset-bottom))] right-4 z-50 flex flex-col gap-2 items-end">
              {/* Add Stock */}
              <button
                className="flex items-center gap-2 bg-white rounded-full pl-3 pr-4 py-2 shadow-lg border"
                onClick={() => {
                  openActionModal('restock');
                  setFabMenuOpen(false);
                }}
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <Plus className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium">Add Stock</span>
              </button>
              
              {/* Bulk Restock - Owner only */}
              {isOwner && (
                <button
                  className="flex items-center gap-2 bg-white rounded-full pl-3 pr-4 py-2 shadow-lg border"
                  onClick={() => {
                    openActionModal('bulk-restock');
                    setFabMenuOpen(false);
                  }}
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Layers className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium">Bulk Restock</span>
                </button>
              )}
            </div>
          )}
          
          {/* FAB Button - smaller on mobile with bolder plus */}
          <Button
            className={`fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4 z-50 w-12 h-12 rounded-full shadow-lg ${fabMenuOpen ? 'bg-muted-foreground rotate-45' : 'bg-primary'} hover:bg-primary/90 transition-all duration-200`}
            onClick={() => setFabMenuOpen(!fabMenuOpen)}
            aria-label={fabMenuOpen ? "Close menu" : "Add Stock"}
          >
            <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
          </Button>
        </div>

        {/* Low Stock Alerts for this vehicle — Stage 6 */}
        <LowStockAlerts sourceType="vehicle" sourceId={id} compact />

        {/* Inventory List */}
        <Card>
          {/* Restock Suggestions — slim banner at top of inventory */}
          <RestockSuggestionsBanner sourceType="vehicle" sourceId={id} />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-bold font-heading">Inventory</CardTitle>
              <span className="text-sm text-muted-foreground/70 tabular-nums pr-1" data-testid="inventory-item-count">{vFilter.query ? `${vFilter.rows.length} of ` : ''}{inventory.length} Items</span>
            </div>
          </CardHeader>
          {inventory.length > 0 && <VehicleInventoryToolbar filter={vFilter} view={vView} />}
          <CardContent className="p-0">
            {inventory.length > 0 && vFilter.rows.length === 0 && (
              <p className="text-center py-8 text-sm text-muted-foreground" data-testid="vehicle-inventory-no-match">No items match “{vFilter.query}”.</p>
            )}
            {inventory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Package className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-semibold text-foreground">No inventory assigned yet</p>
                <p className="text-muted-foreground mt-1 text-center max-w-sm">Add items from your Parts List to this vehicle to track quantities and restock thresholds.</p>
                <p className="text-xs text-muted-foreground mt-3">Items must be created in the Parts List before they can be added here.</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {vFilter.rows.map((inv) => {
                  const isLowStock = inv.reorder_threshold !== null && inv.qty_on_hand <= inv.reorder_threshold && inv.qty_on_hand >= 0;
                  const isNegative = inv.is_negative || inv.qty_on_hand < 0;
                  return (
                    <div
                      key={inv.id}
                      className={`flex items-center justify-between p-4 hover:bg-muted/50 transition-colors ${isNegative ? 'bg-destructive/5' : ''}`}
                      data-testid={`inventory-item-${inv.item_id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {inv.image_thumb_url && (
                            <img src={inv.image_thumb_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" loading="lazy" />
                          )}
                          <p className="font-semibold text-foreground">{inv.item_name}</p>
                          {isNegative && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-destructive/10 text-destructive rounded-full" data-testid={`negative-badge-${inv.item_id}`}>
                              <AlertTriangle className="w-3 h-3" />
                              Negative
                            </span>
                          )}
                          {isLowStock && !isNegative && (
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 bg-warning/10 text-warning rounded-full" data-testid={`low-stock-badge-${inv.item_id}`}>
                              <AlertTriangle className="w-3 h-3" />
                              Low
                            </span>
                          )}
                          {/* Show threshold override indicator */}
                          {inv.reorder_threshold_override !== null && inv.reorder_threshold_override !== undefined && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full" title={`Custom threshold: ${inv.reorder_threshold_override} (default: ${inv.item_reorder_threshold ?? 'none'})`}>
                              <Gauge className="w-3 h-3" />
                              {inv.reorder_threshold_override}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {vCol('sku') && inv.item_sku && (
                            <p className="text-sm text-muted-foreground font-mono">{inv.item_sku}</p>
                          )}
                          {vCol('manufacturer_part_number') && (
                            <p className="text-xs text-muted-foreground" data-testid={`van-mpn-${inv.item_id}`}>
                              <span className="font-medium">Mfr Part #</span>{' '}
                              <span className="font-mono">{inv.item_manufacturer_part_number || '—'}</span>
                            </p>
                          )}
                          {vCol('effective_reorder_threshold') && inv.reorder_threshold !== null && inv.reorder_threshold_override === null && (
                            <p className="text-xs text-muted-foreground">Threshold: {inv.reorder_threshold}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Owner-only Threshold button */}
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`text-muted-foreground hover:text-foreground ${inv.reorder_threshold_override !== null ? 'text-blue-600' : ''}`}
                            onClick={() => openThresholdModal(inv)}
                            data-testid={`threshold-item-${inv.item_id}`}
                            title="Set threshold for this vehicle"
                          >
                            <Gauge className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Owner-only Adjust button per row */}
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => setAdjustmentModal({
                              open: true,
                              itemId: inv.item_id,
                              itemName: inv.item_name,
                              currentQty: inv.qty_on_hand
                            })}
                            data-testid={`adjust-item-${inv.item_id}`}
                            title="Adjust inventory"
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                        )}
                        <button
                          className={`font-mono font-bold px-4 py-2 rounded-full text-lg inline-flex items-center gap-1 group ${
                            isNegative ? 'bg-destructive/10 text-destructive' 
                              : isLowStock ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                              : 'bg-muted text-foreground'
                          }`}
                          onClick={() => openBreakdown(inv.item_id, 'vehicle', id)}
                          title="View availability breakdown"
                          data-testid={`qty-breakdown-${inv.item_id}`}
                        >
                          {inv.qty_on_hand}
                          <Info className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                        {inv.quarantined_qty > 0 && (vCol('available_qty') || vCol('quarantined_qty')) && (
                          <button
                            type="button"
                            onClick={() => openBreakdown(inv.item_id, 'vehicle', id)}
                            className="text-[11px] leading-tight text-right text-amber-700 dark:text-amber-400 whitespace-nowrap"
                            data-testid={`van-qty-detail-${inv.item_id}`}
                          >
                            {vCol('available_qty') && <>{inv.available_qty} available</>}
                            {vCol('available_qty') && vCol('quarantined_qty') && <br />}
                            {vCol('quarantined_qty') && <>{inv.quarantined_qty} quarantined</>}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vehicle Comments Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold font-heading flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments ({comments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Comment List */}
            {comments.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No comments yet. Add notes about this vehicle.
              </p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-muted/50 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{comment.user_name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {format(new Date(comment.created_at), 'MMM d, h:mm a')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-foreground">{comment.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Add Comment Form */}
            <form onSubmit={handleAddComment} className="pt-4 border-t">
              <div className="space-y-3">
                <Textarea
                  placeholder="Add a comment about this vehicle..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                  className="resize-none"
                />
                <div className="flex justify-end">
                  <Button type="submit" size="sm" disabled={submittingComment || !newComment.trim()}>
                    {submittingComment ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Add Comment
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Cycle Count History Section */}
        {canManageCycleCounts && (
          <CycleCountHistory 
            targetType="vehicle" 
            targetId={id}
            returnTo={`/vans/${id}`}
          />
        )}
      </div>

      {/* Edit Van Drawer */}
      <AppDrawer
        open={editDrawerOpen}
        onOpenChange={setEditDrawerOpen}
        title="Edit Vehicle"
        description={`Update details for ${van?.name || 'this vehicle'}`}
        size="md"
        isDirty={
          editData.name !== van?.name ||
          editData.van_number !== (van?.van_number || '') ||
          editData.license_plate !== (van?.license_plate || '') ||
          editData.is_active !== van?.is_active
        }
        data-testid="edit-van-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditDrawerOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Vehicle Name</Label>
            <Input
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              className="h-12"
              data-testid="edit-van-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">Vehicle Number</Label>
            <Input
              value={editData.van_number}
              onChange={(e) => setEditData({ ...editData, van_number: e.target.value })}
              className="h-12"
              data-testid="edit-van-number"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-bold uppercase tracking-wide">License Plate</Label>
            <Input
              value={editData.license_plate}
              onChange={(e) => setEditData({ ...editData, license_plate: e.target.value })}
              className="h-12"
              data-testid="edit-van-plate"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
            <Label className="text-sm font-bold uppercase tracking-wide">Active</Label>
            <Switch
              checked={editData.is_active}
              onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked })}
              data-testid="edit-van-active"
            />
          </div>
        </div>
      </AppDrawer>

      {/* Technician Assignment Drawer */}
      <AppDrawer
        open={techModalOpen}
        onOpenChange={setTechModalOpen}
        title="Assign Technicians"
        description="Select technicians to assign to this vehicle. Previously assigned techs will be reassigned."
        size="md"
        isDirty={JSON.stringify([...selectedTechIds].sort()) !== JSON.stringify(assignedTechs.map(t => t.id).sort())}
        data-testid="assign-techs-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setTechModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTechAssignments} disabled={savingTechs} data-testid="save-tech-assignments-btn">
              {savingTechs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save Assignments
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {allTechs.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No technicians available</p>
          ) : (
            allTechs.map((tech) => {
              const isSelected = selectedTechIds.includes(tech.id);
              const isAssignedElsewhere = tech.assigned_van_id && tech.assigned_van_id !== id;
              
              return (
                <div 
                  key={tech.id} 
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  } ${!tech.is_active ? 'opacity-50' : ''}`}
                  onClick={() => toggleTechSelection(tech.id)}
                >
                  <Checkbox 
                    checked={isSelected}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{tech.name}</p>
                    <p className="text-sm text-muted-foreground">{tech.email}</p>
                    {isAssignedElsewhere && (
                      <p className="text-xs text-orange-600 mt-1">Currently assigned to another vehicle</p>
                    )}
                  </div>
                  {!tech.is_active && (
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded">Inactive</span>
                  )}
                </div>
              );
            })
          )}
        </div>
      </AppDrawer>

      {/* Threshold Override Modal */}
      <Dialog open={thresholdModal.open} onOpenChange={(open) => !open && setThresholdModal({ open: false, itemId: null, itemName: '', currentOverride: null, itemDefault: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Gauge className="w-5 h-5" />
              Set Restock Threshold
            </DialogTitle>
            <DialogDescription>
              Set a custom low-stock threshold for <strong>{thresholdModal.itemName}</strong> on this vehicle only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Item default threshold:</span>
                <span className="font-medium">{thresholdModal.itemDefault ?? 'Not set'}</span>
              </div>
              {thresholdModal.currentOverride !== null && (
                <div className="flex justify-between mt-1">
                  <span className="text-muted-foreground">Current override:</span>
                  <span className="font-medium text-blue-600">{thresholdModal.currentOverride}</span>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="thresholdValue">Vehicle-specific threshold</Label>
              <IntegerQuantityInput
                id="thresholdValue"
                min="0"
                value={thresholdValue}
                onChange={(val) => setThresholdValue(val)}
                placeholder="Leave empty to use item default"
                data-testid="vehicle-threshold-input"
              />
              <p className="text-xs text-muted-foreground">
                When inventory falls at or below this value, it will be marked as low stock.
                Leave empty to reset to item default.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setThresholdModal({ open: false, itemId: null, itemName: '', currentOverride: null, itemDefault: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveThreshold} disabled={savingThreshold}>
              {savingThreshold && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {thresholdValue === '' ? 'Reset to Default' : 'Set Threshold'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Action Modal */}
      <Dialog open={actionModal.open} onOpenChange={(open) => !open && closeModal()}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {actionModal.type === 'use' ? 'Use Item' : 
               actionModal.type === 'transfer' ? 'Transfer Item' : 
               actionModal.type === 'bulk-restock' ? 'Bulk Restock' :
               actionModal.type === 'adjust' ? 'Request Adjustment' : 'Add Stock'}
            </DialogTitle>
            <DialogDescription>
              {actionModal.type === 'adjust' 
                ? 'Submit adjustment for owner approval' 
                : actionModal.type === 'bulk-restock' || actionModal.type === 'restock'
                  ? 'Choose from parts in your Parts List to add to this vehicle.'
                  : 'Manage inventory'}
            </DialogDescription>
          </DialogHeader>
          
          {actionModal.type === 'bulk-restock' ? (
            <div className="space-y-4 mt-4">
              {bulkItems.map((item, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs font-bold uppercase">Item</Label>
                    <AsyncSearchableSelect
                      onSearch={handleItemSearch}
                      value={item.item_id}
                      onValueChange={(val) => { updateBulkItem(index, 'item_id', val); updateBulkItem(index, 'name', ''); }}
                      placeholder="Search by name or SKU…"
                      emptyText="No items found. Try a different search."
                      className="h-10"
                      allowCreate
                      onCreateSelected={(name) => { updateBulkItem(index, 'name', name); updateBulkItem(index, 'item_id', ''); }}
                    />
                  </div>
                  <div className="w-20">
                    <Label className="text-xs font-bold uppercase">Qty</Label>
                    <IntegerQuantityInput
                      min="1"
                      value={item.quantity}
                      onChange={(val) => updateBulkItem(index, 'quantity', val)}
                      className="h-10"
                      data-testid={`van-bulk-qty-${index}`}
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => removeBulkItem(index)} disabled={bulkItems.length === 1}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" onClick={addBulkItem} className="w-full">
                <Plus className="w-4 h-4 mr-2" /> Add Item
              </Button>
              <Button
                className="w-full h-12 text-base font-bold"
                onClick={handleAction}
                disabled={actionLoading}
                data-testid="bulk-submit-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Restock All
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">Select Item *</Label>
                {/* For use/transfer/adjust: show inventory items. For restock: async search */}
                {(actionModal.type === 'use' || actionModal.type === 'transfer' || actionModal.type === 'adjust' || !isOwner) ? (
                  <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                    <SelectTrigger className="h-12 text-lg" data-testid="action-item-select">
                      <SelectValue placeholder="Choose an item" />
                    </SelectTrigger>
                    <SelectContent>
                      {inventory.map((item) => (
                        <SelectItem
                          key={item.item_id}
                          value={item.item_id}
                          className="py-3"
                        >
                          {item.item_name}
                          {(actionModal.type === 'use' || actionModal.type === 'transfer') && ` (${item.qty_on_hand} available)`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <AsyncSearchableSelect
                    onSearch={handleItemSearch}
                    value={selectedItemId}
                    onValueChange={(val) => { setSelectedItemId(val); setCreatePartName(''); }}
                    placeholder="Search by name or SKU…"
                    emptyText="No items found. Try a different search."
                    className="h-12"
                    data-testid="action-item-select"
                    allowCreate={actionModal.type === 'restock'}
                    onCreateSelected={(name) => { setCreatePartName(name); setSelectedItemId(''); }}
                  />
                )}
              </div>

              {actionModal.type === 'transfer' && (
                <div className="space-y-2">
                  <Label className="text-sm font-bold uppercase tracking-wide">Destination Vehicle *</Label>
                  <Select value={selectedToVanId} onValueChange={setSelectedToVanId}>
                    <SelectTrigger className="h-12 text-lg" data-testid="transfer-van-select">
                      <SelectValue placeholder="Choose destination vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {allVans.map((v) => (
                        <SelectItem key={v.id} value={v.id} className="py-3">
                          {v.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  {actionModal.type === 'adjust' ? 'Quantity Change (+ or -) *' : 'Quantity *'}
                </Label>
                <IntegerQuantityInput
                  min={actionModal.type === 'adjust' ? undefined : '1'}
                  value={quantity}
                  onChange={(val) => setQuantity(val)}
                  className="h-12 text-lg font-mono"
                  allowNegative={actionModal.type === 'adjust'}
                  data-testid="action-quantity-input"
                />
                {actionModal.type === 'adjust' && (
                  <p className="text-xs text-muted-foreground">Use negative for decrease, positive for increase</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-bold uppercase tracking-wide">
                  Note {actionModal.type === 'adjust' ? '*' : '(Optional)'}
                </Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={actionModal.type === 'adjust' ? 'Explain the reason for adjustment' : 'e.g., Job #1234'}
                  className="resize-none"
                  data-testid="action-note-input"
                />
              </div>

              <Button
                className="w-full h-12 text-base font-bold btn-press"
                onClick={handleAction}
                disabled={actionLoading || (!selectedItemId && !createPartName) || (actionModal.type === 'transfer' && !selectedToVanId) || (actionModal.type === 'adjust' && (!note || parseInt(quantity) === 0))}
                data-testid="action-submit-btn"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : actionModal.type === 'use' ? 'Use Item' 
                  : actionModal.type === 'transfer' ? 'Transfer'
                  : actionModal.type === 'adjust' ? 'Submit for Approval'
                  : 'Add Stock'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scanner */}
      <ScanManager 
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        context="vehicle"
        vehicleId={id}
        onActionComplete={fetchData}
      />

      {/* Return to Warehouse Modal */}
      <ReturnToWarehouseModal
        open={returnModal.open}
        onClose={(success) => {
          setReturnModal({ open: false, preSelectedItemId: null });
          if (success) fetchData();
        }}
        vanId={id}
        vanName={van?.name || ''}
        inventory={inventory}
        preSelectedItemId={returnModal.preSelectedItemId}
      />

      {/* Manual Adjustment Modal (Owner only) */}
      {isOwner && (
        <ManualAdjustmentModal
          open={adjustmentModal.open}
          onClose={(success) => {
            setAdjustmentModal({ open: false, itemId: null, itemName: '', currentQty: 0 });
            if (success) fetchData();
          }}
          locationType="vehicle"
          locationId={id}
          locationName={van?.name || ''}
          itemId={adjustmentModal.itemId}
          itemName={adjustmentModal.itemName}
          currentQuantity={adjustmentModal.currentQty}
        />
      )}

      {/* Vehicle-to-Vehicle Transfer Request Modal (Tech only) */}
      {isTech && (
        <VehicleToVehicleTransferModal
          open={v2vTransferModal.open}
          onClose={(success) => {
            setV2vTransferModal({ open: false, preSelectedItemId: null });
            if (success) fetchData();
          }}
          sourceVanId={id}
          sourceVanName={van?.name || ''}
          inventory={inventory}
          otherVans={otherVans}
          preSelectedItemId={v2vTransferModal.preSelectedItemId}
        />
      )}

      {/* Cycle Count Modal */}
      {canManageCycleCounts && van && (
        <StartCycleCountModal
          open={cycleCountModalOpen}
          onOpenChange={setCycleCountModalOpen}
          targetType="vehicle"
          targetId={id}
          targetName={van?.name || 'Vehicle'}
          returnTo={`/vans/${id}`}
        />
      )}

      {/* Stage 11 — Auto Restock Review Drawer */}
      <VanRestockReviewDrawer
        open={restockDrawerOpen}
        onOpenChange={setRestockDrawerOpen}
        vanId={id}
        vanName={van?.name}
      />
      <AvailabilityBreakdownModal open={breakdownState.open} onOpenChange={setBreakdownOpen} data={breakdownState.data} loading={breakdownState.loading} />

      {/* Save Template Modal */}
      {isOwner && (
        <SaveTemplateModal
          open={saveTemplateOpen}
          onClose={(success) => {
            setSaveTemplateOpen(false);
          }}
          vehicleId={id}
          vehicleName={van?.name || ''}
          inventoryCount={inventory.length}
        />
      )}

      {/* Apply Template Modal */}
      {isOwner && (
        <ApplyTemplateModal
          open={applyTemplateOpen}
          onClose={(success) => {
            setApplyTemplateOpen(false);
            if (success) fetchData();
          }}
          vehicleId={id}
          vehicleName={van?.name || ''}
        />
      )}

      {/* Clone Inventory Modal */}
      {isOwner && (
        <CloneInventoryModal
          open={cloneInventoryOpen}
          onClose={(success) => {
            setCloneInventoryOpen(false);
            if (success) fetchData();
          }}
          targetVehicleId={id}
          targetVehicleName={van?.name || ''}
          allVans={allVansRaw}
        />
      )}
    </Layout>
  );
};